import './ContactForm.css';
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {Helmet} from "react-helmet";
import { isAuthed, getCurrentUser, refresh } from '../../services/auth.service';
import { useLocation } from 'react-router-dom';
import { saveSiteFeedback , saveContactForm} from '../../utils/api';
import ScrollToTop from '../../components/layout/ScrollToTop';
import { Mail, User, MessageSquare, Send, CheckCircle } from 'lucide-react';

function ContactForm() {
    const search = useLocation().search;
    const [completed, setCompleted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');

    useEffect(() => {
 
        document.title = "LeadrPro - Provide Feedback";
    }, []);
    
    async function saveFeedback(){
      if (feedbackText.length > 0){
        setLoading(true);
        let res = await saveContactForm(name, email, subject, feedbackText);

        if (res.success){
          setCompleted(true);
          setLoading(false);
        }

        else{

        }
      }

      else{
        alert("Message can not be left empty");
      }
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <ScrollToTop/>
          <Helmet>
            <meta name="description" content="Contact LeadrPro - We'd love to hear from you" />
            <title>Contact Us - LeadrPro</title>
          </Helmet>

          <div className="max-w-2xl mx-auto">
            {/* Banner */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">Get in Touch</h1>
              <p className="text-lg text-gray-600">
                Have a question or suggestion? We'd love to hear from you.
              </p>
            </div>

            {completed ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-green-800 mb-2">Message Sent Successfully!</h3>
                <p className="text-green-700">
                  Thanks for reaching out! We'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
                <div className="space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          Name
                        </span>
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          Email
                        </span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-gray-500" />
                          Subject
                        </span>
                      </label>
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500"
                        placeholder="What's this about?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                      <textarea
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        rows={6}
                        className="block w-full rounded-md border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500"
                        placeholder="Your message..."
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={saveFeedback}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>

                  <div className="pt-4 text-center">
                    <p className="text-sm text-gray-600">
                      For urgent support inquiries, please email{' '}
                      <a href="mailto:support@leadrpro.com" className="font-medium text-purple-600 hover:text-purple-500">
                        support@leadrpro.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
    );
}

export default ContactForm;